/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient from './api';

// ==================== ADMIN USERS API ====================

export const adminUsersApi = {
  getUsers: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },

  getUserById: async (id: string | number): Promise<any> => {
    const response = await apiClient.get(`/admin/users/${id}`);
    return response.data;
  },

  updateUser: async (id: string | number, data: any): Promise<any> => {
    const response = await apiClient.put(`/admin/users/${id}`, data);
    return response.data;
  },

  updateUserStatus: async (id: string | number, data: { is_active?: boolean; role?: string }): Promise<any> => {
    const response = await apiClient.patch(`/admin/users/${id}/status`, data);
    return response.data;
  },

  updateUserRole: async (id: string | number, role: string): Promise<any> => {
    const response = await apiClient.patch(`/admin/users/${id}/status`, { role });
    return response.data;
  },

  banUser: async (id: string | number): Promise<any> => {
    const response = await apiClient.patch(`/admin/users/${id}/status`, { is_active: false });
    return response.data;
  },

  activateUser: async (id: string | number): Promise<any> => {
    const response = await apiClient.patch(`/admin/users/${id}/status`, { is_active: true });
    return response.data;
  },
};

// ==================== ADMIN ORDERS API ====================

export const adminOrdersApi = {
  getOrders: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/admin/orders', { params });
    return response.data;
  },

  getOrderById: async (id: string | number): Promise<any> => {
    const response = await apiClient.get(`/admin/orders/${id}`);
    return response.data;
  },

  updateOrderStatus: async (id: string | number, status: string): Promise<any> => {
    const response = await apiClient.patch(`/admin/orders/${id}/status`, { status });
    return response.data;
  },
};

// ==================== ADMIN KITCHEN API ====================
// Routes live at /api/v1/kitchen/* (accessible to ADMIN role)

export const adminKitchenApi = {
  // GET /kitchen/orders/?date=YYYY-MM-DD
  getOrderQueue: async (date?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    const response = await apiClient.get('/kitchen/orders/', { params });
    return response.data;
  },

  // PUT /kitchen/orders/{id}/status
  updateOrderStatus: async (orderId: string | number, status: string): Promise<any> => {
    const response = await apiClient.put(`/kitchen/orders/${orderId}/status`, { status });
    return response.data;
  },

  // GET /kitchen/batches?date=YYYY-MM-DD
  getBatches: async (date?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    const response = await apiClient.get('/kitchen/batches', { params });
    return response.data;
  },

  // PUT /kitchen/batches/{id}
  updateBatch: async (batchId: string | number, data: any): Promise<any> => {
    const response = await apiClient.put(`/kitchen/batches/${batchId}`, data);
    return response.data;
  },
};

// ==================== ADMIN DELIVERY API ====================
// Admin-only delivery management routes at /api/v1/admin/delivery/*

export const adminDeliveryApi = {
  getPartners: async (): Promise<any> => {
    const response = await apiClient.get('/admin/delivery/partners');
    return response.data;
  },

  getAssignments: async (date?: string): Promise<any> => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    const response = await apiClient.get('/admin/delivery/assignments', { params });
    return response.data;
  },

  getZones: async (): Promise<any> => {
    const response = await apiClient.get('/admin/delivery/zones');
    return response.data;
  },
};

// ==================== ADMIN ANALYTICS API ====================
// Routes at /api/v1/admin/analytics/* and /api/v1/admin/dashboard/summary

export const adminAnalyticsApi = {
  // GET /admin/dashboard/summary — key metrics (users, orders, revenue totals)
  getDashboardSummary: async (): Promise<any> => {
    const response = await apiClient.get('/admin/dashboard/summary');
    return response.data;
  },

  // GET /admin/analytics/orders-chart?days=30 — daily order+revenue series
  getOrdersChart: async (days = 30): Promise<any> => {
    const response = await apiClient.get('/admin/analytics/orders-chart', { params: { days } });
    return response.data;
  },

  // GET /admin/analytics/meal-tracking — meal popularity stats
  getMealTracking: async (): Promise<any> => {
    const response = await apiClient.get('/admin/analytics/meal-tracking');
    return response.data;
  },

  // GET /admin/analytics/delivery — delivery zone performance
  getDeliveryAnalytics: async (): Promise<any> => {
    const response = await apiClient.get('/admin/analytics/delivery');
    return response.data;
  },

  // Alias for backward compatibility with adminSlice and AdminDashboardPage
  getDashboardStats: async (): Promise<any> => {
    const response = await apiClient.get('/admin/dashboard/summary');
    return response.data;
  },
};

// ==================== ADMIN MENUS API ====================
// Menu management via /api/v1/kitchen/* endpoints (ADMIN has kitchen_manager access)

export const adminMenusApi = {
  // GET /kitchen/menus — list all menus with items
  getWeeklyMenus: async (statusOrParams?: string | Record<string, any>): Promise<any> => {
    const params: Record<string, string> = {};
    if (typeof statusOrParams === 'string') {
      params.status = statusOrParams;
    } else if (statusOrParams?.status) {
      params.status = statusOrParams.status;
    }
    const response = await apiClient.get('/kitchen/menus', { params });
    return response.data;
  },

  // POST /kitchen/menu — create a new weekly menu (draft)
  createMenu: async (data: any): Promise<any> => {
    const response = await apiClient.post('/kitchen/menu', data);
    return response.data;
  },

  // PUT /kitchen/menu/{id}/publish — publish a draft menu
  publishMenu: async (id: string | number): Promise<any> => {
    const response = await apiClient.put(`/kitchen/menu/${id}/publish`);
    return response.data;
  },
};

// ==================== ADMIN SUBSCRIPTIONS API ====================
// Plan management at /api/v1/admin/subscription-tiers
// User subscriptions at /api/v1/subscriptions/admin/list

export const adminSubscriptionsApi = {
  // GET /admin/subscription-tiers — list all plans
  getPlans: async (): Promise<any> => {
    const response = await apiClient.get('/admin/subscription-tiers');
    return response.data;
  },

  // POST /admin/subscription-tiers — create new plan
  createPlan: async (data: any): Promise<any> => {
    const response = await apiClient.post('/admin/subscription-tiers', data);
    return response.data;
  },

  // PUT /admin/subscription-tiers/{id} — update plan (e.g., toggle active)
  updatePlan: async (id: string | number, data: any): Promise<any> => {
    const response = await apiClient.put(`/admin/subscription-tiers/${id}`, data);
    return response.data;
  },

  // GET /subscriptions/admin/list — all user subscriptions (admin view)
  getUserSubscriptions: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/subscriptions/admin/list', { params });
    return response.data;
  },

  // GET /admin/orders — billing records (orders with payment info)
  getBillingHistory: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/admin/orders', { params: { ...params, page_size: 50 } });
    return response.data;
  },
};

// ==================== ADMIN RECIPES API ====================

export const adminRecipesApi = {
  getRecipes: async (params?: Record<string, any>): Promise<any> => {
    const response = await apiClient.get('/admin/recipes', { params });
    return response.data;
  },

  createRecipe: async (data: any): Promise<any> => {
    const response = await apiClient.post('/admin/recipes', data);
    return response.data;
  },

  updateRecipe: async (id: string | number, data: any): Promise<any> => {
    const response = await apiClient.put(`/admin/recipes/${id}`, data);
    return response.data;
  },

  deleteRecipe: async (id: string | number): Promise<void> => {
    await apiClient.delete(`/admin/recipes/${id}`);
  },
};

